import { useMemo, useCallback } from 'react';
import {
  VisibilityState,
  Updater,
  functionalUpdate,
} from '@tanstack/react-table';
import {
  TableName,
  TableSettings,
  useGetUserPreferences,
  userPreferenceKeys,
  useUpdateUserPreferences,
} from '@sib/api';

export const useTablePreferencesSettings = (tableName: TableName) => {
  const { data } = useGetUserPreferences({
    key: userPreferenceKeys.tableSettings,
  });

  const { mutate } = useUpdateUserPreferences();

  const tableSettings = useMemo<TableSettings>(
    () =>
      (data?.preferences?.[userPreferenceKeys.tableSettings] ??
        {}) as TableSettings,
    [data?.preferences]
  );

  const columnVisibility = useMemo<VisibilityState>(
    () => tableSettings[tableName]?.visibility ?? {},
    [tableSettings, tableName]
  );

  const hasLoadedPreferences = !!data;

  const saveColumnVisibility = useCallback(
    (partial: VisibilityState) => {
      if (!hasLoadedPreferences) return;

      const merged: VisibilityState = {
        ...columnVisibility,
        ...partial,
      };

      const updatedTableSettings: TableSettings = {
        ...tableSettings,
        [tableName]: {
          ...tableSettings[tableName],
          visibility: merged,
        },
      };

      mutate({
        key: userPreferenceKeys.tableSettings,
        query: { key: userPreferenceKeys.tableSettings },
        data: { value: updatedTableSettings },
      });
    },
    [hasLoadedPreferences, columnVisibility, tableSettings, tableName, mutate]
  );

  const handleColumnVisibilityChange = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      const nextVisibility = functionalUpdate(
        updaterOrValue,
        columnVisibility ?? {}
      );
      saveColumnVisibility(nextVisibility);
    },
    [saveColumnVisibility, columnVisibility]
  );

  return {
    onColumnVisibilityChange: handleColumnVisibilityChange,
    state: {
      columnVisibility: hasLoadedPreferences ? columnVisibility : null,
    },
  };
};
